'use client';

import { useEffect, useState } from 'react';
import {
  getHotJobKeywords,
  getJobSearchSuggestions,
  JOBS_MAX_QUERY_LENGTH,
} from './index';

const SEARCH_SUGGESTIONS_DEBOUNCE_MS = 300;

type QuerySuggestionState = {
  isLoading: boolean;
  keywords: string[];
  query: string;
  sessionVersion: number;
};

type HotKeywordState = {
  keywords: string[];
  sessionVersion: number;
};

function normalizeKeywords(values: string[]): string[] {
  return values
    .filter(value => typeof value === 'string')
    .map(value => value.trim())
    .filter(Boolean);
}

export function useJobSearchSuggestions(
  query: string,
  enabled: boolean,
  sessionVersion = 0
) {
  const [hotKeywordState, setHotKeywordState] = useState<HotKeywordState>({
    keywords: [],
    sessionVersion: -1,
  });
  const [querySuggestionState, setQuerySuggestionState] =
    useState<QuerySuggestionState>({
      isLoading: false,
      keywords: [],
      query: '',
      sessionVersion: -1,
    });
  const normalizedQuery = query.trim().slice(0, JOBS_MAX_QUERY_LENGTH);

  useEffect(() => {
    if (enabled) return;

    const resetTimer = window.setTimeout(() => {
      setHotKeywordState({ keywords: [], sessionVersion: -1 });
      setQuerySuggestionState({
        isLoading: false,
        keywords: [],
        query: '',
        sessionVersion: -1,
      });
    }, 0);

    return () => window.clearTimeout(resetTimer);
  }, [enabled, sessionVersion]);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    void getHotJobKeywords(controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setHotKeywordState({
            keywords: normalizeKeywords(response.data),
            sessionVersion,
          });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setHotKeywordState({ keywords: [], sessionVersion });
        }
      });

    return () => controller.abort();
  }, [enabled, sessionVersion]);

  useEffect(() => {
    if (!enabled || !normalizedQuery) return;

    const controller = new AbortController();

    const debounceTimer = window.setTimeout(() => {
      setQuerySuggestionState({
        isLoading: true,
        keywords: [],
        query: normalizedQuery,
        sessionVersion,
      });

      void getJobSearchSuggestions(normalizedQuery, controller.signal)
        .then(response => {
          if (!controller.signal.aborted) {
            setQuerySuggestionState({
              isLoading: false,
              keywords: normalizeKeywords(response.data),
              query: normalizedQuery,
              sessionVersion,
            });
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setQuerySuggestionState({
              isLoading: false,
              keywords: [],
              query: normalizedQuery,
              sessionVersion,
            });
          }
        });
    }, SEARCH_SUGGESTIONS_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [enabled, normalizedQuery, sessionVersion]);

  const hasCurrentQueryResult =
    querySuggestionState.query === normalizedQuery &&
    querySuggestionState.sessionVersion === sessionVersion;
  const hasCurrentHotKeywords =
    hotKeywordState.sessionVersion === sessionVersion;

  return {
    isLoading:
      enabled &&
      Boolean(normalizedQuery) &&
      (!hasCurrentQueryResult || querySuggestionState.isLoading),
    keywords: enabled
      ? normalizedQuery
        ? hasCurrentQueryResult
          ? querySuggestionState.keywords
          : []
        : hasCurrentHotKeywords
          ? hotKeywordState.keywords
          : []
      : [],
  };
}
